import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ymd, ukDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const KIND = { break: "Break", holiday: "Holiday", sickness: "Sickness", other: "Blocked / closed", extra: "Extra working hours" };
const when = (b) => `${ukDate(b.start_date)}${b.end_date !== b.start_date ? ` – ${ukDate(b.end_date)}` : ""} · ${b.all_day ? "All day" : `${b.start}–${b.end}`}`;

async function removeBlock(b, onDone) {
  try { await api.delete(`/blockouts/${b.id}`); toast.success(`${KIND[b.kind || "other"]} deleted`); onDone(); }
  catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
}

export function BlockDetailDialog({ block, canDelete, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm w-[calc(100vw-1.5rem)] rounded-2xl" data-testid="block-detail-dialog">
        <DialogHeader><DialogTitle>{KIND[block.kind || "other"]}</DialogTitle></DialogHeader>
        <div className="text-sm space-y-1">
          <div className="font-medium">{block.staff_name || "Whole salon"}</div>
          <div className="text-stone-600" data-testid="block-detail-when">{when(block)}</div>
          {block.reason && <div className="text-stone-500">{block.reason}</div>}
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="ghost" className="rounded-full" onClick={onClose}>Close</Button>
          {canDelete && <Button data-testid="block-detail-delete" disabled={busy} className="rounded-full bg-rose-600 hover:bg-rose-700"
            onClick={async () => { setBusy(true); await removeBlock(block, () => { onChanged(); onClose(); }); setBusy(false); }}>
            <Trash2 className="h-4 w-4 mr-1.5" />Delete</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TimeOffListDialog({ canDelete, onClose, onChanged }) {
  const [items, setItems] = useState(null);
  const load = async () => {
    const start = ymd(new Date()); const end = new Date(); end.setFullYear(end.getFullYear() + 1);
    const { data } = await api.get("/blockouts", { params: { start, end: ymd(end) } });
    setItems(data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg w-[calc(100vw-1.5rem)] max-h-[85vh] overflow-y-auto rounded-2xl" data-testid="time-off-dialog">
        <DialogHeader><DialogTitle>Block-outs & time off</DialogTitle></DialogHeader>
        {!items ? <p className="text-sm text-stone-400">Loading…</p> : items.length === 0 ? (
          <p className="text-sm text-stone-500" data-testid="time-off-empty">Nothing booked off in the next 12 months.</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {items.map((b) => (
              <div key={b.id} data-testid={`time-off-${b.id}`} className="py-3 flex items-start gap-3">
                <div className="flex-1 min-w-0 text-sm">
                  <div className="font-medium">{KIND[b.kind || "other"]} · {b.staff_name || "Whole salon"}</div>
                  <div className="text-stone-600">{when(b)}</div>
                  {b.reason && <div className="text-xs text-stone-500 truncate">{b.reason}</div>}
                </div>
                {canDelete(b) && <Button size="sm" variant="ghost" data-testid={`time-off-delete-${b.id}`} className="rounded-full text-rose-600"
                  onClick={() => window.confirm("Delete this?") && removeBlock(b, () => { load(); onChanged(); })}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-stone-500">Regular breaks are set per person in Staff → Working hours.</p>
      </DialogContent>
    </Dialog>
  );
}
